// ModalRegistroRepeat.js
import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { toast } from 'react-toastify';
import { ApiService } from './ApiService';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
};

export const ModalRegistroRepeat = ({
  open,
  onClose,
  email,
  onVerified
}) => {
  // step=1 => “Enviando código...”
  // step=2 => “Ingresar los 6 dígitos”
  // step=3 => “Cuenta Verificada”
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState(['', '', '', '', '', '']); 
  const [verifying, setVerifying] = useState(false);

  const inputsRef = useRef([]);

  useEffect(() => {
    if (open) {
      // Reset al abrir
      setStep(1);
      setLoading(true);
      setCode(['', '', '', '', '', '']);
      sendCode(); // Llamamos a /sendCode de inmediato
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Paso 1 => POST /sendCode
  const sendCode = async () => {
    try {
      const body = { email_User: email.toLowerCase() };
      const response = await ApiService.fetchData('api/auth/sendCode', {
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (!response || response.code !== 200) {
        throw new Error(response?.message || 'Error al enviar el código');
      }

      // Ok => Paso 2
      setLoading(false);
      setStep(2);

    } catch (err) {
      setLoading(false);
      toast.error(err.message, { position: 'top-right' });
      onClose(); // cierra modal si falla
    }
  };

  // Manejo dígitos 6
  const handleDigitChange = (index, value) => {
    if (/^\d?$/.test(value)) {
      const newArr = [...code];
      newArr[index] = value;
      setCode(newArr);

      if (value && index < 5) {
        inputsRef.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      if (code[index]) {
        const newArr = [...code];
        newArr[index] = '';
        setCode(newArr);
      } else if (index > 0) {
        inputsRef.current[index - 1]?.focus();
        const newArr = [...code];
        newArr[index - 1] = '';
        setCode(newArr);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('Text');
    if (/^\d{6}$/.test(pasted)) {
      const arr = pasted.split('');
      setCode(arr);
      inputsRef.current[5]?.focus();
    }
  };

  // Paso 2 => POST /verifyCode
  const handleVerify = async () => {
    setVerifying(true);
    try {
      const digits = code.join('');
      if (digits.length < 6) {
        throw new Error('Completa los 6 dígitos');
      }

      const body = {
        code: digits,
        email_User: email.toLowerCase()
      };
      const response = await ApiService.fetchData('api/auth/verifyCode', {
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (!response) {
        throw new Error('Error al verificar el código');
      }

      // Ok => Paso 3
      setStep(3);
    } catch (err) {
      toast.error(err.message, { position: 'top-right' });
    } finally {
      setVerifying(false);
    }
  };

  // Paso 3 => Aceptar => cierra modal y llama onVerified
  const handleAccept = () => {
    onClose();
    onVerified?.();
  };

  return (
    <Modal
      open={open}
      onClose={(_, reason) => {
        // Deshabilitamos cerrar por ESC o click en backdrop
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      disableEscapeKeyDown
      hideBackdrop={false}
      BackdropProps={{
        style: { pointerEvents: 'none' }
      }}
    >
      <Box sx={style}>

        <Box
          mb={2}
          sx={{
            bgcolor: step === 3 ? '#2e7d32' : '#d32f2f',
            p: 1.5
          }}
        >
          <Typography variant="h6" sx={{ color: 'white', m: 0 }}>
            {step === 1
              ? 'Enviando Código'
              : step === 2
                ? 'Verificación de Correo'
                : 'Cuenta Verificada'
            }
          </Typography>
        </Box>

        {/* Paso 1 => spinner */}
        {step === 1 && (
          <Box textAlign="center" p={2}>
            <Typography variant="body2" sx={{ fontSize: 16 }}>
              Por favor no cierres esta página
            </Typography>
            <Box display="flex" justifyContent="center" mt={3} mb={3}>
              {loading && <CircularProgress />}
            </Box>
          </Box>
        )}

        {/* Paso 2 => ingresar 6 dígitos */}
        {step === 2 && (
          <>
            <Typography
              variant="body2"
              gutterBottom
              sx={{ textAlign: 'center', fontSize: 16 }}
            >
              Coloca los 6 dígitos enviados a tu Correo
            </Typography>
            <Box
              display="flex"
              gap={1}
              justifyContent="center"
              mt={2}
              onPaste={handlePaste}
            >
              {code.map((digit, idx) => (
                <TextField
                  key={idx}
                  value={digit}
                  inputRef={(el) => (inputsRef.current[idx] = el)}
                  onChange={(e) => handleDigitChange(idx, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, idx)}
                  inputProps={{ maxLength: 1, style: { textAlign: 'center' } }}
                  sx={{ width: 40 }}
                />
              ))}
            </Box>

            <Box mt={3} textAlign="center" mb={2}>
              <Button
                variant="contained"
                onClick={handleVerify}
                disabled={verifying}
                sx={{ backgroundColor: '#d32f2f' }}
              >
                {verifying ? 'Cargando...' : 'Verificar'}
              </Button>
            </Box>
          </>
        )}

        {/* Paso 3 => Exito */}
        {step === 3 && (
          <Box textAlign="center" p={2}>
            <Box display="flex" justifyContent="center" mb={2}>
              <CheckCircleOutlineIcon sx={{ color: '#4caf50', fontSize: 48 }} />
            </Box>
            <Typography variant="h6" sx={{ fontSize: '18px', mb: 1 }}>
              ¡Cuenta Verificada!
            </Typography>
            <Typography variant="body2" sx={{ fontSize: 16, mb: 2 }}>
              Ahora puedes iniciar sesión
            </Typography>
            <Button
              variant="contained"
              onClick={handleAccept}
              sx={{ backgroundColor: '#4caf50' }}
            >
              Aceptar
            </Button>
          </Box>
        )}

      </Box>
    </Modal>
  );
};
